import { useTranslation } from 'react-i18next';
import {
  ArrowDownToLine,
  ArrowRightLeft,
  ArrowUpFromLine,
  Percent,
  Pencil,
  ShoppingCart,
  Undo2,
  type LucideIcon,
} from 'lucide-react';
import { Skeleton, TruncatedAmount } from '@shared/ui';
import { formatMoneyRu, type ServiceAccountingSummary } from '@entities/service-accounting';
import { formatAssetNetworkLabel, getAssetBalanceKey } from '@shared/lib/asset-balance';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';

interface SummaryCardsProps {
  summary: ServiceAccountingSummary;
  isLoading?: boolean;
}

type SummaryCardKey = Exclude<keyof ServiceAccountingSummary, 'byAsset'>;

interface SummaryCardItem {
  key: SummaryCardKey;
  icon: LucideIcon;
  iconClassName: string;
  count?: boolean;
}

const ITEMS: SummaryCardItem[] = [
  { key: 'totalDeposits', icon: ArrowDownToLine, iconClassName: 'bg-emerald-500/15 text-emerald-600' },
  { key: 'totalWithdrawals', icon: ArrowUpFromLine, iconClassName: 'bg-red-500/15 text-red-600' },
  { key: 'exchangeCount', icon: ArrowRightLeft, iconClassName: 'bg-blue-500/15 text-blue-600', count: true },
  { key: 'commissions', icon: Percent, iconClassName: 'bg-amber-500/15 text-amber-700' },
  { key: 'expenses', icon: ShoppingCart, iconClassName: 'bg-orange-500/15 text-orange-700' },
  { key: 'refunds', icon: Undo2, iconClassName: 'bg-violet-500/15 text-violet-700' },
  { key: 'adjustmentCount', icon: Pencil, iconClassName: 'bg-slate-500/15 text-slate-700', count: true },
];

export function SummaryCards({ summary, isLoading = false }: SummaryCardsProps) {
  const { t } = useTranslation('cabinet');

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-7">
        {ITEMS.map((item) => (
          <Skeleton key={item.key} className="h-16 min-w-[140px] w-full rounded-lg sm:h-20 sm:min-w-[156px] sm:rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4 xl:grid-cols-7">
        {ITEMS.map((item) => {
          const Icon = item.icon;
          const value = summary[item.key];
          const displayValue = item.count ? String(value) : formatMoneyRu(value);
          return (
            <div key={item.key} className="stat-card min-w-[140px] sm:min-w-[156px]">
              <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-full sm:h-9 sm:w-9 ${item.iconClassName}`}>
                <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </div>
              <h3 className="stat-card-label">{t(`serviceAccounting.summary.${item.key}`)}</h3>
              <TruncatedAmount value={displayValue} className="stat-card-value mt-0.5" />
            </div>
          );
        })}
      </div>
      {summary.byAsset.length > 0 ? (
        <section className="space-y-2">
          <h3 className="section-title">{t('serviceAccounting.summary.byAssetTitle')}</h3>
          <div className="flex flex-wrap gap-2">
            {summary.byAsset.map((row) => (
              <div
                key={getAssetBalanceKey(row.assetCode, row.network)}
                className="rounded-lg border border-border bg-card px-3 py-2 text-sm"
              >
                <span className="font-semibold">
                  {formatLedgerAmount(row.total, { assetLabel: row.assetCode })}
                </span>
                <span className="ml-2 text-xs text-muted-foreground">
                  {formatAssetNetworkLabel(row.network, t('serviceAccounting.historicalNetwork'))}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
